import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Button, makeStyles } from "@fluentui/react-components";
import { BordersAll, BordersHorizontal, BordersVertical, BordersNone, BordersTable } from "../../../assets/icons";
import { add, remove } from "../../../state/bordersSlice";
import { batchGroupBy } from "../../../helpers/batchGroupBy";

const useClasses = makeStyles({
  PanelButton: {
    justifyContent: "left",
    fontFamily: "Arial",
    fontSize: 9,
    fontWeight: 400,
    letterSpacing: "0.02em",
  },
});

const SelectionBordersMenuItem = () => {
  const classes = useClasses();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const selection = useSelector((state) => state.selection);
  const structure = useSelector((state) => state.gridd.present.structure);
  const borders = useSelector((state) => state.gridd.present.borders);

  const getBounds = () => {
    if (selection.rows.length > 0 && selection.columns.length === 0) {
      return {
        minRow: Math.min(...selection.rows),
        maxRow: Math.max(...selection.rows),
        minCol: 0,
        maxCol: structure.columns.length - 1,
      };
    }
    if (selection.columns.length > 0 && selection.rows.length === 0) {
      return {
        minRow: 0,
        maxRow: structure.rows.length - 1,
        minCol: Math.min(...selection.columns),
        maxCol: Math.max(...selection.columns),
      };
    }
    const xs = selection.cells.map((c) => c.x);
    const ys = selection.cells.map((c) => c.y);
    return {
      minRow: Math.min(...xs),
      maxRow: Math.max(...xs),
      minCol: Math.min(...ys),
      maxCol: Math.max(...ys),
    };
  };

  const addHorizontalBorders = (minRow, maxRow, minCol, maxCol) => {
    for (let i = minRow + 1; i <= maxRow; i++) {
      dispatch(add({ rowIndex: i, from: minCol, to: maxCol + 1 }));
    }
  };

  const addVerticalBorders = (minRow, maxRow, minCol, maxCol) => {
    for (let i = minCol + 1; i <= maxCol; i++) {
      dispatch(add({ columnIndex: i, from: minRow, to: maxRow + 1 }));
    }
  };

  const addAroundBorders = (minRow, maxRow, minCol, maxCol) => {
    dispatch(add({ columnIndex: minCol, from: minRow, to: maxRow + 1 }));
    dispatch(add({ columnIndex: maxCol + 1, from: minRow, to: maxRow + 1 }));
    dispatch(add({ rowIndex: minRow, from: minCol, to: maxCol + 1 }));
    dispatch(add({ rowIndex: maxRow + 1, from: minCol, to: maxCol + 1 }));
  };

  return (
    <div
      style={{
        display: "grid",
        gridTemplateRows: "auto auto auto auto auto",
      }}
    >
      <Button
        id="selection_horizontal_borders_button"
        icon={<BordersHorizontal />}
        appearance="subtle"
        className={classes.PanelButton}
        onClick={() => {
          const { minRow, maxRow, minCol, maxCol } = getBounds();
          batchGroupBy.start();
          addHorizontalBorders(minRow, maxRow, minCol, maxCol);
          batchGroupBy.end();
        }}
      >
        {t("menu.table.borders.horizontal")}
      </Button>
      <Button
        id="selection_vertical_borders_button"
        icon={<BordersVertical />}
        appearance="subtle"
        className={classes.PanelButton}
        onClick={() => {
          const { minRow, maxRow, minCol, maxCol } = getBounds();
          batchGroupBy.start();
          addVerticalBorders(minRow, maxRow, minCol, maxCol);
          batchGroupBy.end();
        }}
      >
        {t("menu.table.borders.vertical")}
      </Button>
      <Button
        id="selection_around_borders_button"
        icon={<BordersTable />}
        appearance="subtle"
        className={classes.PanelButton}
        onClick={() => {
          const { minRow, maxRow, minCol, maxCol } = getBounds();
          batchGroupBy.start();
          addAroundBorders(minRow, maxRow, minCol, maxCol);
          batchGroupBy.end();
        }}
      >
        {t("menu.table.borders.table")}
      </Button>
      <Button
        id="selection_all_borders_button"
        icon={<BordersAll />}
        appearance="subtle"
        className={classes.PanelButton}
        onClick={() => {
          const { minRow, maxRow, minCol, maxCol } = getBounds();
          batchGroupBy.start();
          addAroundBorders(minRow, maxRow, minCol, maxCol);
          addHorizontalBorders(minRow, maxRow, minCol, maxCol);
          addVerticalBorders(minRow, maxRow, minCol, maxCol);
          batchGroupBy.end();
        }}
      >
        {t("menu.table.borders.all")}
      </Button>
      <Button
        id="selection_clear_borders_button"
        icon={<BordersNone />}
        appearance="subtle"
        className={classes.PanelButton}
        onClick={() => {
          const { minRow, maxRow, minCol, maxCol } = getBounds();
          batchGroupBy.start();
          borders.forEach((b) => {
            const bFrom = b.from ?? 0;
            const bTo = b.to ?? Number.MAX_SAFE_INTEGER;
            if (!b.isVertical) {
              if (b.index >= minRow && b.index <= maxRow + 1 && bFrom < maxCol + 1 && bTo > minCol) {
                dispatch(remove({ id: b.id }));
              }
            } else {
              if (b.index >= minCol && b.index <= maxCol + 1 && bFrom < maxRow + 1 && bTo > minRow) {
                dispatch(remove({ id: b.id }));
              }
            }
          });
          batchGroupBy.end();
        }}
      >
        {t("menu.table.borders.none")}
      </Button>
    </div>
  );
};

export default SelectionBordersMenuItem;
